import { FC } from 'react';
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Box, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';

import { TrblAddIcon, TrblClipboardIcon, TrblDeleteIcon } from '@/components/Icons';

import { formatDate, GRID_FOCUS_SX } from '../constants';
import { ScopeChips } from './KeysGrid';

import adminStyles from '../../AdministrationPage/styles.module.scss';
import styles from '../styles.module.scss';

export type ServerRow = {
  id: string;
  name: string;
  url: string;
  scopes: string[];
  isBuiltIn: boolean;
  shared?: boolean;
  canDelete: boolean;
  createdAt?: string;
};

type ServersGridProps = {
  servers: ServerRow[];
  isLoading: boolean;
  onCreateKey: (server: ServerRow) => void;
  onDelete: (server: ServerRow) => void;
};

const copyUrl = (url: string) => {
  navigator.clipboard.writeText(url).then(
    () => toast.success('Server URL copied'),
    () => toast.error("Couldn't copy the server URL")
  );
};

export const ServersGrid: FC<ServersGridProps> = ({ servers, isLoading, onCreateKey, onDelete }) => {
  const columns: GridColDef<ServerRow>[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params: GridRenderCellParams<ServerRow>) => (
        <span>
          {params.row.name} {params.row.isBuiltIn && <span className={styles['chip']}>Built-in</span>}
          {params.row.shared === false && <span className={styles['chip']}>Admins only</span>}
        </span>
      ),
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'url',
      headerName: 'Server URL',
      flex: 1,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams<ServerRow>) => (
        <>
          <code className={styles['prefix']}>{params.row.url}</code>
          <Tooltip title="Copy server URL">
            <GridActionsCellItem
              icon={<TrblClipboardIcon />}
              label="Copy server URL"
              onClick={() => copyUrl(params.row.url)}
            />
          </Tooltip>
        </>
      ),
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'scopes',
      headerName: 'Access',
      width: 260,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ServerRow>) => <ScopeChips scopes={params.row.scopes} />,
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 110,
      valueFormatter: (params) => formatDate(params.value),
      headerClassName: adminStyles['data-grid-header'],
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<ServerRow>) => (
        <div className={adminStyles['row-actions-cell']}>
          <Tooltip title="Create API key for this server">
            <GridActionsCellItem
              icon={<TrblAddIcon fill="none" />}
              label="Create API key"
              onClick={() => onCreateKey(params.row)}
            />
          </Tooltip>
          {params.row.canDelete && (
            <Tooltip title="Delete server">
              <GridActionsCellItem icon={<TrblDeleteIcon />} label="Delete" onClick={() => onDelete(params.row)} />
            </Tooltip>
          )}
        </div>
      ),
      headerClassName: adminStyles['data-grid-header'],
    },
  ];

  return (
    <Box component="div" height="100%" width="100%">
      <DataGrid
        sx={{
          ...GRID_FOCUS_SX,
          '& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator': { display: 'none' },
        }}
        rows={servers}
        loading={isLoading}
        columns={columns}
        autoPageSize
        disableRowSelectionOnClick
        disableColumnSelector
        rowHeight={42}
        columnHeaderHeight={42}
        slotProps={{ row: { className: adminStyles['data-grid-row'] } }}
      />
    </Box>
  );
};
